import { CheckCircle } from '@mui/icons-material';
import { Alert, AlertTitle, styled, Tooltip, Typography } from '@mui/material';
import { FrontEndSiteUrlToStageMap } from 'adminConstants/redirectConstant';
import PageSection from 'components/new/PageSection';
import { useLayoutContext } from 'contexts/LayoutContext';
import Avatar from 'further-ui/components/Avatar';
import { Card, CardContent } from 'further-ui/components/Card';
import Checkbox from 'further-ui/components/Checkbox';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column, Row } from 'further-ui/components/Layout';
import TypographyButton from 'further-ui/components/TypographyButton';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

const InstitutionCard = styled(Card)({
  maxWidth: '350px',
});

type Props = {
  onClose: () => void;
  onRetry: () => void;
};

const ProcessBankAccount: React.FC<Props> = ({ onClose, onRetry }) => {
  const { selectedVehicleId } = useLayoutContext();
  const [selectedAccounts, setSelectedAccounts] = useState<Array<string>>([]);
  const [hasAgreedToTerms, setHasAgreedToTerms] = useState(false);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const requisitionId = searchParams.get('ref');
  const bankAccountId = searchParams.get('bankAccountId');

  const baseUrl = FrontEndSiteUrlToStageMap[process.env.REACT_APP_STAGE ?? ''];

  const [data] = api.bankFeed.getAvailableBankAccounts.useSuspenseQuery({
    requisitionId: requisitionId ?? '',
    vehicleId: selectedVehicleId ?? '',
  });

  const addBankAccountsFromBankFeed =
    api.bankAccount.addBankAccountsFromBankFeed.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Accounts added successfully.',
        onSuccess: onClose,
      }),
    );

  const handleAddAccounts = () => {
    if (!data || !selectedAccounts.length) return;

    const accounts = data.filter((account) =>
      selectedAccounts.includes(account.bankFeedAccountId),
    );

    addBankAccountsFromBankFeed.mutate({
      accounts,
      bankAccountId: bankAccountId ?? undefined,
      vehicleId: selectedVehicleId ?? '',
    });
  };

  const pageTitle = 'Select bank account(s)';

  if (!requisitionId || !data.length) {
    return (
      <ConfirmDialog
        open
        title={pageTitle}
        onClose={onClose}
        content={
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            We can't find the account you're looking for.{' '}
            <TypographyButton
              text="Click here to try again"
              onClick={onRetry}
            />
            .
          </Alert>
        }
      />
    );
  }

  const handleSelectAccount = (accountId: string) => {
    if (addBankAccountsFromBankFeed.isPending) return;

    // If we are connecting a bank account to the feed, we can add only one account
    if (bankAccountId) {
      setSelectedAccounts([accountId]);
      return;
    }

    if (selectedAccounts.includes(accountId)) {
      setSelectedAccounts(selectedAccounts.filter((id) => id !== accountId));
    } else {
      setSelectedAccounts([...selectedAccounts, accountId]);
    }
  };

  return (
    <ConfirmDialog
      open
      title={pageTitle}
      onClose={onClose}
      loading={addBankAccountsFromBankFeed.isPending}
      maxWidth="sm"
      actions={{
        confirm: {
          onClick: handleAddAccounts,
          label: bankAccountId ? 'Add account' : 'Add accounts',
          disabled: !selectedAccounts.length || !hasAgreedToTerms,
        },
        cancel: {
          onClick: onClose,
        },
      }}
      content={
        <Column spacing="lg">
          <PageSection>
            <InstitutionCard variant="outlined">
              <CardContent size="sm">
                <Row centered justify="space-between">
                  <Row centered spacing="xs">
                    <Avatar src={data[0].bankFeedInstitutionLogo} size="lg" />
                    <Column>
                      <Typography variant="h6">{data[0].bankName}</Typography>
                      <Typography variant="body2">
                        {data[0].accountHolderName}
                      </Typography>
                    </Column>
                  </Row>
                  <CheckCircle fontSize="medium" color="primary" />
                </Row>
              </CardContent>
            </InstitutionCard>
          </PageSection>
          <Typography variant="body2">
            Please select one or more account below to connect it to Further.
          </Typography>
          <Column spacing="sm">
            {data?.map((account) => {
              const isAccountAlreadyConnected =
                account.isAccountAlreadyConnected &&
                account.connectedBankAccountId !== bankAccountId;

              return (
                <Tooltip
                  key={account.bankFeedAccountId}
                  title={
                    isAccountAlreadyConnected
                      ? 'This account is already connected to Further.'
                      : ''
                  }
                >
                  <Card
                    variant="outlined"
                    disabled={isAccountAlreadyConnected}
                    onClick={() =>
                      isAccountAlreadyConnected
                        ? null
                        : handleSelectAccount(account.bankFeedAccountId)
                    }
                  >
                    <CardContent size="sm">
                      <Row fullWidth justify="space-between" centered>
                        <Column spacing="xxs">
                          <Typography variant="h6">
                            {account.accountName} ({account.accountCurrency})
                          </Typography>
                          <Typography variant="body2">
                            IBAN {account.iban}
                          </Typography>
                        </Column>
                        <span onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            onChange={() =>
                              isAccountAlreadyConnected
                                ? null
                                : handleSelectAccount(account.bankFeedAccountId)
                            }
                            checked={selectedAccounts.includes(
                              account.bankFeedAccountId,
                            )}
                            disabled={isAccountAlreadyConnected}
                            labelPosition="before"
                            size="small"
                            label="Read-only access"
                          />
                        </span>
                      </Row>
                    </CardContent>
                  </Card>
                </Tooltip>
              );
            })}
            <Checkbox
              size="small"
              checked={hasAgreedToTerms}
              onChange={(e) => setHasAgreedToTerms(e.target.checked)}
              label={
                <>
                  I agree to the{' '}
                  <TypographyButton
                    text="Further Terms of Service"
                    href={`${baseUrl}/terms-conditions`}
                  />{' '}
                  and{' '}
                  <TypographyButton
                    text="Privacy Policy"
                    href={`${baseUrl}/privacy-policy`}
                  />
                  .
                </>
              }
            />
          </Column>
        </Column>
      }
    />
  );
};

export default ProcessBankAccount;
